import React, { useContext, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { isEmail } from 'react-multi-email';
import Button from 'components/button';
import Modal from 'components/modal';
import {
  HeaderContainer,
  ModalActions,
  ModalContainer,
  ModalContent,
  ModalSection,
  ModalSectionTitle,
} from 'components/modal/styles';
import { MODAL_SIZES } from 'components/modal/types';
import Notification from 'components/notification';
import { setUserLicense, setUserTeams as setUserTeamsAction } from 'components/usersTable/actions';
import { getDomain } from 'constants/utilities/emails';
import {
  LICENSE_REJECTION_OUTCOMES,
  licensesNotChanged,
} from 'constants/errors/license_error_codes';
import { NOTIFICATION_TYPES } from 'constants/components/notification';
import {
  EMAILS_NOT_CHECKED,
  INVITATIONS_NOT_SENT,
  activeUsersInvitedToTeam,
  activeUsersNotInvitedToTeam,
  pendingUsersInvitedToTeam,
  usersNotMadeManagers,
} from 'constants/invites';
import { LICENSE_UPDATE_FAILED } from 'constants/licenses';
import { Domains } from 'contexts/accounts/types';
import { SessionContext } from 'contexts/session';
import { DeepTeams, Team, UserTeam } from 'contexts/teams/types';
import { Invite } from 'pages/Users/components/InviteUsers/AddByEmails/invitesReducer';
import AdjustUsersSettings from 'pages/Users/components/InviteUsers/AdjustUsersSettings';
import EmailInput from 'pages/Users/components/InviteUsers/EmailInput';
import RemoveUsers from 'pages/Users/components/InviteUsers/RemoveUsers/RemoveUsers';
import { getAlreadyInvited } from 'services/alreadyInviteds';
import { sendUserInvites } from 'services/userInvites';
import {
  applySkillDiveDependency,
  buildInvitesWithTeams,
  isRefusal,
  withLiveAccount,
} from 'utils/invites';
import { FEATURE_FLAGS, useBooleanFlag } from 'hooks/useFeatureFlags';
import useLicenseRejection from 'hooks/useLicenseRejection';
import TeamInfo from './TeamInfo';
import UsersSuggestions from './UsersSuggestions';

interface IInviteToTeam {
  isVisible?: boolean;
  editTeam?: boolean;
  team?: Team | DeepTeams;
  onClose?: (ok?: string | boolean) => void;
  isInvite?: boolean;
  account?: AccountInvite;
}

interface AccountInvite {
  id: string;
  licenses_available?: number;
  email_domains?: Domains[];
}

// Only a license rejection keeps the modal open, so the admin can adjust and retry.
type EmailInvitesOutcome = 'sent' | 'license_rejected' | 'failed';

const InviteToTeam: React.FC<IInviteToTeam> = ({
  isVisible,
  team,
  onClose,
  isInvite,
  account: openedWithAccount,
}) => {
  const [activeUsers, setActiveUsers] = useState<Invite[]>([]);
  const [emailInvites, setEmailInvites] = useState<Invite[]>([]);
  const [emails, setEmails] = useState([]);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState([]);
  const [suggestions, areSuggestions] = useState<boolean>(true);
  const [inviteEditMobile, setinviteEditMobile] = useState(false);
  const [disableSaveInvitations, setDisableSaveInvitations] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userTeams, setUserTeams] = useState<UserTeam[]>([]);
  const [savedActiveUsers, setSavedActiveUsers] = useState(false);
  const {
    account: sessionAccount,
    actions: { refreshAccounts, refreshSession },
  } = useContext(SessionContext);
  const skillDiveFlow = useBooleanFlag(FEATURE_FLAGS.SKILL_DIVE_LICENSE_FLOW);
  const notifyLicenseRejection = useLicenseRejection();
  const account = withLiveAccount(openedWithAccount, sessionAccount);

  const validAccountDomain = (email) => {
    const domain = getDomain(email);
    return account?.email_domains.some(({ email_domain }) => email_domain === domain);
  };

  const validEmails = emails.filter((email) => isEmail(email) && validAccountDomain(email));

  const modalSize =
    (step === 0 && MODAL_SIZES.SMALL) ||
    (step == 1 && MODAL_SIZES.LARGE) ||
    (step === 2 && MODAL_SIZES.LARGE) ||
    (step === 3 && MODAL_SIZES.SMALL);

  const setupDefaultLicense = (license_already_added: boolean) => {
    if (
      (typeof account.licenses_available === 'boolean' && account.licenses_available === true) ||
      license_already_added
    )
      return true;
    if (account.licenses_available >= validEmails.length) return true;
    return false;
  };

  const handleActiveUsersSelection = (data) => {
    const activeUserInvite = {
      ...data,
      userTeams,
      // Current access, for the license locks and to send only changes.
      existing: {
        user: data.user,
        uaa_id: data.uaa_id,
        is_team_manager: data.is_team_manager,
        has_license: data.has_license,
        license_addons: data.license_addons ?? [],
        teams: (data.teams ?? []).map(({ team_id, is_manager }) => ({ team_id, is_manager })),
      },
    };
    const newUserInvites = data.checked
      ? [...activeUsers, activeUserInvite]
      : activeUsers.filter(({ id }) => id !== activeUserInvite.id);

    setActiveUsers(newUserInvites);
  };

  const handleUserInvites = () => {
    handleValidateEmails();
  };

  const handleValidateEmails = async () => {
    setLoading(true);
    try {
      const response = await getAlreadyInvited(emails);
      let errors = [];
      if (response?.status === 200) {
        const alreadyInvited = response.data.already_invited.length > 0;
        if (alreadyInvited) {
          const alreadyInvited = handleAlreadyInvited(response.data.already_invited);
          errors = [...errors, ...alreadyInvited];
        }
      } else {
        // @ToDo handle errors properly
        return;
      }
      const invalidDomain = handleInvalidDomains();
      errors = [...errors, ...invalidDomain];
      setErrors((prevState) => [...prevState, ...errors]);
      setStep(1);
    } catch (error) {
      Notification({ text: EMAILS_NOT_CHECKED, type: NOTIFICATION_TYPES.ERROR });
    } finally {
      setLoading(false);
    }
  };

  const handleInvalidDomains = () => {
    const invitationInvalidDomain = emails
      .map((email) => {
        if (isEmail(email) && !validAccountDomain(email)) {
          return {
            email: email,
            error: "Failed: The email address doesn't match with account valid domains",
          };
        } else return null;
      })
      .filter((item) => item);
    return invitationInvalidDomain;
  };

  const handleAlreadyInvited = (alreadyInvited) => {
    const initialErrors = [];
    const invitationErrors = alreadyInvited.map((invitation) => {
      let error = '';
      if (invitation.account?.id !== account.id) {
        error =
          'Failed: This email address is associated with another account, check if the email is already associated with an account';
      } else {
        if (invitation.is_active) {
          error =
            invitation.teams.length > 0
              ? `Failed: This email address is already associated to teams ${invitation.teams
                  .map((team) => team.name)
                  .join(', ')}`
              : 'Failed: This email address is already associated with this account';
        } else {
          error =
            invitation.teams.length > 0
              ? `Failed: This email address has already been invited to teams ${invitation.teams
                  .map((team) => team.name)
                  .join(', ')}`
              : 'Failed: This email address has already been invited checks if you have another invite active';
        }
      }
      if (
        !initialErrors.includes(invitation.email) &&
        !errors
          .filter((item) => item)
          .map(({ email }) => email)
          .includes(invitation.email)
      ) {
        initialErrors.push(invitation.email);
        return {
          email: invitation.email,
          error: error,
        };
      }
    });
    return invitationErrors;
  };

  const handleRemoveErrors = () => {
    const invalids = errors.map((error) => error.email);
    const newEmails = emails.filter((email) => !invalids.includes(email) && isEmail(email));
    setErrors([]);
    setEmails(newEmails);
    if (newEmails.length < 1 && activeUsers.length < 1) {
      setStep(0);
    }
  };

  const saveActiveUserSettings = async () => {
    const teamsPayload = [];
    const userIdsWithLicense = [];
    const userIdsWithNoLicense = [];
    // A Team Manager can't change another manager's license, so managers are made last.
    const promotions = [];
    activeUsers.map((activeUser) => {
      const otherTeams = activeUser.teams.reduce((acc, team) => {
        acc.push({ is_manager: team.is_manager, team_id: team.team_id });
        return acc;
      }, []);
      const licenseChanges = activeUser.has_license !== activeUser.existing?.has_license;
      const promotesLast = Boolean(activeUser.userTeams[0].is_manager) && licenseChanges;
      const teamsWith = (isManager: boolean) => [
        ...otherTeams,
        { is_manager: isManager, team_id: activeUser.userTeams[0].team_id },
      ];
      teamsPayload.push({
        user_invite_id: activeUser.id,
        teams: teamsWith(promotesLast ? false : activeUser.userTeams[0].is_manager),
      });
      if (promotesLast) {
        promotions.push({
          index: teamsPayload.length - 1,
          license: activeUser.has_license,
          payload: { user_invite_id: activeUser.id, teams: teamsWith(true) },
        });
      }
      // Send only licenses that changed.
      if (licenseChanges) {
        activeUser.has_license
          ? userIdsWithLicense.push(activeUser.id)
          : userIdsWithNoLicense.push(activeUser.id);
      }
    });

    const teamsResponses = await Promise.all(
      teamsPayload.map(async (tp) => {
        return await setUserTeamsAction({
          user_invite_id: tp.user_invite_id,
          teams: tp.teams,
        });
      })
    );
    let teamRespOk = 0;
    let teamRespError = 0;
    teamsResponses.map((resp) => {
      if (!isRefusal(resp)) {
        teamRespOk++;
      } else {
        teamRespError++;
      }
    });
    teamRespOk > 0 &&
      Notification({
        text: activeUsersInvitedToTeam(teamRespOk, team.name),
        type: NOTIFICATION_TYPES.SUCCESS,
      });
    teamRespError > 0 &&
      Notification({
        text: activeUsersNotInvitedToTeam(teamRespError, team.name),
        type: NOTIFICATION_TYPES.ERROR,
      });

    const setNoLicense =
      userIdsWithNoLicense.length &&
      (await setUserLicense({ ids: userIdsWithNoLicense, license: false }));
    const setLicense =
      userIdsWithLicense.length &&
      (await setUserLicense({ ids: userIdsWithLicense, license: true }));

    [
      { response: setNoLicense, assign: false },
      { response: setLicense, assign: true },
    ].forEach(({ response, assign }) => {
      if (isRefusal(response)) {
        notifyLicenseRejection(response, {
          outcome: licensesNotChanged(assign),
          fallback: LICENSE_UPDATE_FAILED,
        });
      }
    });

    const promotionResponses = await Promise.all(
      promotions
        .filter(({ index }) => !isRefusal(teamsResponses[index]))
        .map(({ license, payload }) =>
          isRefusal(license ? setLicense : setNoLicense) ? null : setUserTeamsAction(payload)
        )
    );
    const notPromoted = promotionResponses.filter(
      (response) => response === null || isRefusal(response)
    ).length;
    notPromoted > 0 &&
      Notification({
        text: usersNotMadeManagers(notPromoted, team.name),
        type: NOTIFICATION_TYPES.ERROR,
      });
  };

  const sendEmailInvites = async (invitesPayload: Invite[]): Promise<EmailInvitesOutcome> => {
    try {
      const invitesResponse = await sendUserInvites({
        message: `You're being invited to ${team.name}`,
        invites: invitesPayload,
      });
      if (invitesResponse?.status >= 200) {
        Notification({
          text: pendingUsersInvitedToTeam(invitesPayload.length, team.name),
          type: NOTIFICATION_TYPES.SUCCESS,
        });
        // The Skill Dive seats these invites reserved come off the counts shown elsewhere.
        if (invitesPayload.some(({ has_skill_dive }) => has_skill_dive)) {
          refreshSession();
          refreshAccounts();
        }
        return 'sent';
      }
      Notification({ text: INVITATIONS_NOT_SENT, type: NOTIFICATION_TYPES.ERROR });
      return 'failed';
    } catch (error) {
      const licenseError = notifyLicenseRejection(error, {
        outcome: LICENSE_REJECTION_OUTCOMES.NO_EMAIL_INVITATIONS_SENT,
        fallback: INVITATIONS_NOT_SENT,
      });
      return licenseError ? 'license_rejected' : 'failed';
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    const hadActiveUsers = activeUsers.length > 0;
    hadActiveUsers && (await saveActiveUserSettings());
    let emailOutcome: EmailInvitesOutcome = 'sent';
    if (emailInvites.length) {
      // The payload drops `userTeams`, so it isn't written back: the access step stays mounted
      // while the send is in flight and renders each invitee's teams from it.
      emailOutcome = await sendEmailInvites(
        buildInvitesWithTeams(emailInvites, { withSkillDive: skillDiveFlow })
      );
    }
    setLoading(false);
    if (emailOutcome !== 'license_rejected') {
      onClose(true);
      return;
    }
    if (hadActiveUsers) {
      // Those changes already went through, so a retry only resends the email batch.
      setSavedActiveUsers(true);
      setActiveUsers([]);
    }
  };

  const handleAdjustSettings = (invitesUpdated) => {
    const activeUserUpdated = invitesUpdated.filter((i) => i.id);
    const emailInvitesUpdated = invitesUpdated.filter((i) => !i.id);
    activeUserUpdated.length > 0 && setActiveUsers(activeUserUpdated);
    emailInvitesUpdated.length > 0 &&
      setEmailInvites(applySkillDiveDependency(emailInvitesUpdated));
  };

  const handleRemoveInvite = (emailToRemove) => {
    const newEmailInvites = emailInvites.filter(({ email }) => email !== emailToRemove);
    setEmailInvites(newEmailInvites);
    setEmails(newEmailInvites.map(({ email }) => email));
    const newActiveUsers = activeUsers.filter(({ email }) => email !== emailToRemove);
    setActiveUsers(newActiveUsers);

    if (!newEmailInvites.length && !newActiveUsers.length) {
      setStep(0);
    }
  };

  const handleUseLicenses = (usedLicenses) => {
    setDisableSaveInvitations(usedLicenses > account.licenses_available); // revisar este dato si en accounts siempre figura igual
  };

  useEffect(() => {
    const userTeams = [
      {
        team: team,
        team_id: team.id,
        name: team.name,
        is_manager: false,
      },
    ];
    setUserTeams(userTeams);
  }, [team]);

  useEffect(() => {
    if (!userTeams.length) return;
    setEmailInvites((prevInvites) => {
      if (!prevInvites.length) return prevInvites;
      return prevInvites.map((invite) =>
        invite.userTeams ? invite : { ...invite, userTeams: userTeams }
      );
    });
  }, [userTeams]);

  useEffect(() => {
    let newInvites = [];
    validEmails.map((email) => {
      const existingInvite = emailInvites.find((invite) => invite.email === email);
      const { is_manager = false, has_license = false } = existingInvite || {};
      newInvites = [
        ...newInvites,
        {
          email,
          is_manager: is_manager,
          has_license: setupDefaultLicense(has_license),
          userTeams: userTeams,
          // Removing one invitee rebuilds the list; keep the others' Skill Dive and per-team
          // manager choices.
          ...(skillDiveFlow &&
            existingInvite && {
              has_skill_dive: existingInvite.has_skill_dive,
              userTeams: existingInvite.userTeams || userTeams,
            }),
        },
      ];
    });
    setEmailInvites(applySkillDiveDependency(newInvites));
  }, [emails]);

  return (
    <Modal
      centered
      visible={isVisible}
      fullscreen={isMobile}
      onCancel={() => (savedActiveUsers ? onClose(true) : onClose())}
      footer={null}
      maskClosable={false}
      closeIconColor="#FFF"
      width={isMobile ? '100%' : modalSize}
    >
      <ModalContainer
        style={isMobile ? { maxHeight: '100%', height: '100%', overflow: 'hidden' } : {}}
      >
        <HeaderContainer>
          {isInvite ? <h2>Invite users to the team</h2> : <h2>Your team has been created!</h2>}
        </HeaderContainer>
        {step === 0 && (
          <>
            <ModalContent
              grow
              flexColumn
              style={isMobile ? { height: '100%', maxHeight: 'calc(100% - 211px)' } : {}}
            >
              <TeamInfo team={team} />
              {!isInvite && <ModalSectionTitle>Invite users to your team</ModalSectionTitle>}
              {suggestions && (
                <ModalSection style={isMobile ? { overflow: 'hidden' } : {}}>
                  <UsersSuggestions
                    onSelect={handleActiveUsersSelection}
                    checkeds={activeUsers.map((item) => item.id)}
                    areSuggestions={areSuggestions}
                    teamId={team.id}
                  />
                </ModalSection>
              )}
              <ModalSection toBottom>
                <EmailInput
                  title={suggestions && 'Not listed above? Invite by email.'}
                  placeholder={'Add multiple emails separated by a comma (,).'}
                  emails={emails}
                  setEmails={setEmails}
                />
              </ModalSection>
            </ModalContent>
            <ModalActions>
              {!isInvite ? (
                <>
                  <Button
                    $secondary
                    $full={isMobile}
                    onClick={() => (savedActiveUsers ? onClose(true) : onClose())}
                  >
                    I’ll do this later
                  </Button>
                  <Button
                    onClick={handleUserInvites}
                    $full={isMobile}
                    loading={loading}
                    disabled={emails.some((email) => !isEmail(email)) && activeUsers.length === 0}
                  >
                    Invite users
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleUserInvites}
                  loading={loading}
                  disabled={emails.some((email) => !isEmail(email)) && activeUsers.length === 0}
                >
                  Next
                </Button>
              )}
            </ModalActions>
          </>
        )}

        {step === 1 && errors.length > 0 && (
          <>
            <ModalContent>
              <RemoveUsers wrongEmails={errors} />
            </ModalContent>
            <ModalActions>
              <Button onClick={handleRemoveErrors}>Remove errors</Button>
            </ModalActions>
          </>
        )}

        {step === 1 && errors.length < 1 && (
          <>
            <ModalContent style={isMobile ? { maxHeight: 'calc(100% - 210px)' } : {}}>
              <TeamInfo team={team} />
              <AdjustUsersSettings
                hasTeam={Boolean(userTeams.length > 0)}
                invites={emailInvites}
                activeUserInvites={activeUsers}
                onInvitesUpdate={handleAdjustSettings}
                onInviteRemove={handleRemoveInvite}
                onEdit={setinviteEditMobile}
                isEditing={inviteEditMobile}
                onLicenseExcess={handleUseLicenses}
                footer={inviteEditMobile}
                account={account}
                teamImplied
              />
            </ModalContent>
            {!inviteEditMobile && (
              <ModalActions>
                <Button
                  onClick={handleSaveSettings}
                  disabled={disableSaveInvitations}
                  loading={loading}
                >
                  Save changes
                </Button>
              </ModalActions>
            )}
          </>
        )}
      </ModalContainer>
    </Modal>
  );
};

export default InviteToTeam;
